import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { Request } from 'express';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import tinycolor from 'tinycolor2';
import { getRGBColor } from 'app/shared/style';
import { LOADER_PRESET_TOKEN } from 'app/shared/full-loader.component';
import {
    extractRequestHostname,
    getWindowHostname,
    getWindowOrigin,
    isBrowser,
} from 'app/core/utils/platform.util';
import CryptoJS from 'crypto-js';
export type KnowledgebaseDomainErrorCode = 404 | 410;

@Injectable({ providedIn: 'root' })
export class ApiService {
    checkSubscriotion = new BehaviorSubject<boolean>(true);
    isSubscriptionOver = new BehaviorSubject<boolean>(false);
    baseUrl: any = environment.baseUrl;
    isLoggedOut = new BehaviorSubject<number>(0);
    userPermission = new BehaviorSubject<any>([]);

    isLoadCategoryKb = new BehaviorSubject<boolean>(false);
    kbCategoryData: any;
    kbCategoryLoaded = false;
    domainBlocked = false;
    knowledgebaseErrorCode: KnowledgebaseDomainErrorCode = 404;
    tenantHostname: string | null = null;
    private domainConfigRequest$: Observable<any> | null = null;

    constructor(
        private httpClient: HttpClient,
        private _router: Router,
        @Optional() @Inject(REQUEST) private _request: Request | null,
    ) {
        this.setBaseUrl();
    }

    isDomainBlocked() {
        return this.domainBlocked;
    }

    setKnowledgebaseErrorCode(statusCode: KnowledgebaseDomainErrorCode) {
        this.knowledgebaseErrorCode = statusCode;
    }

    getKnowledgebaseErrorCode(): KnowledgebaseDomainErrorCode {
        return this.knowledgebaseErrorCode;
    }

    isAllowedWhenDomainBlocked(url: string) {
        return url.includes('get-domain') || url.includes('assets/i18n/') || url.includes('/i18n/');
    }

    handleDomainUnavailable(statusCode: KnowledgebaseDomainErrorCode) {
        if (!isBrowser()) {
            return;
        }

        if (this.domainBlocked) {
            return;
        }

        this.domainBlocked = true;
        this.navigateToKnowledgebaseError(statusCode);
    }

    navigateToKnowledgebaseError(statusCode: KnowledgebaseDomainErrorCode = 404) {
        this.setKnowledgebaseErrorCode(statusCode);

        if (!isBrowser()) {
            return;
        }

        const url = this._router.url.split('?')[0].split('#')[0];
        if (url.startsWith('/404')) {
            return;
        }

        void this._router.navigate(['/404'], { replaceUrl: true });
    }

    setBaseUrl(tenantName?: string) {
        const domain = environment.domainType;
        let host: string;
        const localDomain = (environment as any).localDomain;

        if (localDomain) {
            host = localDomain.replace(/^https?:\/\//, '').replace(/\/+$/, '');
        } else if (domain === 'dev') {
            host = tenantName ? tenantName : 'backend.deskusend.com';
        } else if (domain === 'io') {
            host = tenantName ? tenantName : 'backend.deskudocs.com';
        } else {
            host = 'demo2.deskudocs.local';
        }

        this.baseUrl = `https://${host}/api/`;
    }

    getRequestOrigin() {
        if (!this._request) {
            return '';
        }

        const hostHeader =
            this._request.headers['x-forwarded-host'] || this._request.headers.host;
        const host = Array.isArray(hostHeader) ? hostHeader[0] : hostHeader;
        if (!host) {
            return '';
        }

        const protocolHeader = this._request.headers['x-forwarded-proto'];
        const protocol = Array.isArray(protocolHeader)
            ? protocolHeader[0]
            : protocolHeader || 'https';

        return `${protocol}://${host}`.split('#')[0];
    }

    getDomainRegistryBaseUrl() {
        const localDomain = (environment as any).localDomain;
        if (localDomain) {
            const host = localDomain.replace(/^https?:\/\//, '').replace(/\/+$/, '');
            return `https://${host}/api/`;
        }

        const configured = environment.seo?.getDomainApiUrl;
        if (configured) {
            return configured.endsWith('/') ? configured : `${configured}/`;
        }

        if (environment.domainType === 'dev') {
            return 'https://backend.deskusend.com/api/';
        }

        if (environment.domainType === 'io') {
            return 'https://backend.deskudocs.com/api/';
        }

        return 'https://demo2.deskudocs.local/api/';
    }

    applyTenantFromDomainResponse(response: any) {
        const tenantName = response?.data?.tenant_name ?? response?.tenant_name;
        this.tenantHostname = tenantName || null;
        this.setBaseUrl(tenantName);
    }

    getDomainConfig(forceRefresh = false) {
        if (forceRefresh) {
            this.domainConfigRequest$ = null;
        }

        if (this.domainConfigRequest$) {
            return this.domainConfigRequest$;
        }

        const localDomain = (environment as any).localDomain;
        if (localDomain) {
            const host = localDomain.replace(/^https?:\/\//, '').replace(/\/+$/, '');
            this.tenantHostname = host;
            this.setBaseUrl(host);
            this.domainConfigRequest$ = of({ success: true, tenant_name: host });
            return this.domainConfigRequest$;
        }

        const domain = getWindowOrigin() || this.getRequestOrigin();
        const defaultTenantName = getWindowHostname() || extractRequestHostname(this._request);

        this.domainConfigRequest$ = this.httpClient
            .post(`${this.getDomainRegistryBaseUrl()}get-domain`, { domain }, {
                responseType: 'text',
            })
            .pipe(
                map((body) => this.parseJsonBody(body)),
                switchMap((response: any) => {
                    if (response == null) {
                        return throwError(() => ({ status: 404 }));
                    }

                    if (response?.success === false) {
                        this.handleDomainUnavailable(410);
                        return throwError(() => ({
                            status: 410,
                            error: response,
                        }));
                    }

                    this.applyTenantFromDomainResponse(response);
                    return of(response);
                }),
                catchError((error) => {
                    this.domainConfigRequest$ = null;
                    const status = (error as HttpErrorResponse)?.status as
                        | KnowledgebaseDomainErrorCode
                        | undefined;

                    if (status === 410 || status === 404) {
                        this.handleDomainUnavailable(status);
                        return throwError(() => error);
                    }

                    this.tenantHostname = defaultTenantName || null;
                    this.setBaseUrl(defaultTenantName);
                    return throwError(() => error);
                }),
                shareReplay({ bufferSize: 1, refCount: false }),
            );

        return this.domainConfigRequest$;
    }

    parseJsonBody(body: string | null) {
        if (body == null || body.trim() === '') {
            return null;
        }

        try {
            return JSON.parse(body);
        } catch {
            return null;
        }
    }

    requestJson<T = any>(request$: Observable<string>): Observable<T | null> {
        return request$.pipe(
            map((body) => this.parseJsonBody(body)),
            catchError((err: HttpErrorResponse | Error) => {
                if (err instanceof HttpErrorResponse) {
                    if (err.status === 0 || err.status === 404 || err.status >= 500) {
                        return of(null);
                    }
                }
                return throwError(() => err);
            }),
        );
    }

    getData(data: string) {
        return this.requestJson(
            this.httpClient.get(this.baseUrl + data, { responseType: 'text' }),
        );
    }

    postData(url: string, data: unknown) {
        return this.requestJson(
            this.httpClient.post(this.baseUrl + url, data, { responseType: 'text' }),
        );
    }

    computeColorPalette(color, themeClass?) {
        let passedColor = color;

        document.body.style.setProperty(`--desku-primary`, color);
        document.body.style.setProperty(
            `--desku-primary-rgb`,
            getRGBColor(color, 'primary').slice(0, -1)
        );
        document.body.style.setProperty(
            `--desku-primary-500-rgb`,
            getRGBColor(color, 'primary').slice(0, -1)
        );
        document.body.style.setProperty(`--desku-primary`, color);
        document.body.style.setProperty('--primary', color);
        document.body.style.setProperty('--desku-theme', color);
        document.body.style.setProperty(
            '--mdc-linear-progress-active-indicator-color',
            color
        );
        document.body.style.setProperty(
            '--mdc-circular-progress-active-indicator-color',
            color
        );
        document.body.style.setProperty(
            '--mdc-radio-selected-icon-color',
            color
        );
        if (themeClass == 'theme-desku')
            color = localStorage.getItem('themeColor') ?? passedColor;
        return [
            this.getColorObject(tinycolor(color).lighten(45), '50', themeClass),
            this.getColorObject(
                tinycolor(color).lighten(40),
                '100',
                themeClass
            ),
            this.getColorObject(
                tinycolor(color).lighten(30),
                '200',
                themeClass
            ),
            this.getColorObject(
                tinycolor(color).lighten(20),
                '300',
                themeClass
            ),
            this.getColorObject(
                tinycolor(color).lighten(10),
                '400',
                themeClass
            ),
            this.getColorObject(tinycolor(color), '500', themeClass),
            this.getColorObject(tinycolor(color).darken(10), '600', themeClass),
            this.getColorObject(tinycolor(color).darken(20), '700', themeClass),
            this.getColorObject(tinycolor(color).darken(30), '800', themeClass),
            this.getColorObject(tinycolor(color).darken(40), '900', themeClass),
        ];
    }
    getColorObject(
        value: tinycolor.Instance,
        name: string,
        themeClass: string
    ): any {
        setTimeout(() => {
            const c = tinycolor(value);
            let t = setInterval(() => {
                document.documentElement.style.setProperty(
                    `--desku-primary-${name}-rgb`,
                    getRGBColor(c.toHexString(), 'primary').slice(0, -1)
                );
                document.documentElement.style.setProperty(
                    `--desku-primary-${name}`,
                    c.toHexString()
                );
                if (name == '500') {
                    document.documentElement.style.setProperty(
                        `--desku-primary-rgb`,
                        getRGBColor(c.toHexString(), 'primary').slice(0, -1)
                    );
                    document.documentElement.style.setProperty(
                        `--desku-primary`,
                        c.toHexString()
                    );
                    document.documentElement.style.setProperty(
                        '--primary',
                        c.toHexString()
                    );
                    document.documentElement.style.setProperty(
                        '--desku-theme',
                        c.toHexString()
                    );
                    document.documentElement.style.setProperty(
                        '--mdc-linear-progress-active-indicator-color',
                        c.toHexString()
                    );
                    document.documentElement.style.setProperty(
                        '--mdc-circular-progress-active-indicator-color',
                        c.toHexString()
                    );
                    document.documentElement.style.setProperty(
                        '--mdc-radio-selected-icon-color',
                        c.toHexString()
                    );
                }
                if (document.getElementsByClassName(themeClass)) {
                    let classArray = Array.from(
                        document.getElementsByClassName(
                            themeClass
                        ) as HTMLCollectionOf<HTMLElement>
                    );
                    classArray.forEach((abc: any) => {
                        abc.style.setProperty(
                            `--desku-primary-${name}-rgb`,
                            getRGBColor(c.toHexString(), 'primary').slice(0, -1)
                        );
                        abc.style.setProperty(
                            `--desku-primary-${name}`,
                            c.toHexString()
                        );
                        if (name == '500') {
                            abc.style.setProperty(
                                `--desku-primary`,
                                c.toHexString()
                            );
                            abc.style.setProperty(
                                '--desku-primary-rgb',
                                getRGBColor(c.toHexString(), 'primary').slice(
                                    0,
                                    -1
                                )
                            );
                        }
                    });
                    // console.log(name, themeClass, c.toHexString())
                    clearInterval(t);
                }
            }, 1000);
            return {
                name,
                hex: c.toHexString(),
                rgb: c.toRgbString(),
                rgbCode: getRGBColor(c.toHexString(), 'primary').slice(0, -1),
                isDarkContrast: c.isLight(),
            };
        }, 10);
    }

    static convertDecryptData(data: any): string {
        if (data) {
            let part1: string = (environment as any)?.appSessionSeed || '';
            let part2: string = LOADER_PRESET_TOKEN || '';
            let part3: string = (Math.sqrt(9)).toString() + 'desku'.charAt(3);
            let finalKey: string | null = part1 + part2 + part3;

            try {
                const decData = CryptoJS.AES.decrypt(data, finalKey);
                finalKey = null;
                part1 = '';
                part2 = '';
                part3 = '';
                return decData.toString(CryptoJS.enc.Utf8);
            } catch {
                finalKey = null;
                part1 = '';
                part2 = '';
                part3 = '';
                return '';
            }
        }
        return '';
    }

}
